/**
 * ErrorBanner Component
 *
 * A full-width error banner that appears at the top of the viewport.
 * Used for displaying important error messages to users.
 *
 * Features:
 * - Fixed position at top (overlay, doesn't push content)
 * - Slide-down animation on enter, slide-up on exit
 * - Auto-dismiss after specified duration (default 3 seconds)
 * - Manual dismiss via close button
 * - Brand-consistent error styling
 */

import React, { useEffect, useState } from 'react';

export interface ErrorBannerProps {
  message: string;
  onDismiss: () => void;
  duration?: number; // Auto-dismiss duration in ms (default 3000)
  visible?: boolean;
}

const ErrorBanner: React.FC<ErrorBannerProps> = ({
  message,
  onDismiss,
  duration = 3000,
  visible = true,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  // Handle auto-dismiss
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => clearTimeout(timer);
  }, [visible, duration]);

  // Animate out before dismissing
  const handleDismiss = () => {
    setIsExiting(true);
    // Wait for exit animation to complete before calling onDismiss
    setTimeout(() => {
      onDismiss();
    }, 250); // Match exit animation duration
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] ${
        isExiting ? 'error-banner-exit' : 'error-banner-enter'
      }`}
      role="alert"
      aria-live="assertive"
    >
      <div className="bg-red-50 border-b border-red-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            {/* Icon and Message */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Warning Icon */}
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Message */}
              <p className="text-sm font-medium text-red-800 truncate">
                {message}
              </p>
            </div>

            {/* Close Button */}
            <div className="flex-shrink-0">
              <button
                type="button"
                onClick={handleDismiss}
                className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50 transition-colors"
                aria-label="Dismiss error"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorBanner;
